import React from "react";
import {
  Check,
  FileText,
  CreditCard,
  User,
  Building,
  DollarSign,
  Globe,
} from "lucide-react";
import { getVisibleFields, getVisibleDocuments } from "utilities/schemaEval";

const RegistrationSummary = ({ data, schema, onSubmit }) => {
  const { businessInfo, owners, dbas, bankingInfo, processingInfo, documents } =
    data;

  // Schema-driven summary sections for flat steps
  const flatStepSummaries = (schema?.steps || [])
    .filter((step) => !step.sectionType)
    .map((step) => {
      const section = data?.[step.key] || {};
      const visibleFields = getVisibleFields(step.fields, section, data);
      const details = visibleFields
        .map((f) => {
          const val = section[f.name];
          if (!val || String(val).trim() === "") return null;
          return `${f.label}: ${val}`;
        })
        .filter(Boolean);

      const iconMap = {
        businessInfo: <Building className="h-5 w-5" />,
        bankingInfo: <CreditCard className="h-5 w-5" />,
        processingInfo: <DollarSign className="h-5 w-5" />,
      };

      return {
        icon: iconMap[step.key] || <FileText className="h-5 w-5" />,
        title: step.title,
        details,
      };
    });

  // DBA summary
  const dbaSummary = {
    icon: <Globe className="h-5 w-5" />,
    title: "URLs & DBAs",
    details: (dbas || []).flatMap((dba, i) => {
      const lines = [];
      if (dba.dbaName) lines.push(`DBA ${i + 1}: ${dba.dbaName}`);
      if (dba.websiteUrl) lines.push(`Website: ${dba.websiteUrl}`);
      if (dba.adminEmail) lines.push(`Admin: ${dba.adminEmail}`);
      return lines;
    }),
  };

  // Owners summary
  const ownerSummary = {
    icon: <User className="h-5 w-5" />,
    title: "Owner Information",
    details: (owners || []).flatMap((owner, i) => {
      const lines = [];
      const label = owner.ownerType === "signer" ? "Signer" : `Owner ${i + 1}`;
      if (owner.fullName) lines.push(`${label}: ${owner.fullName}`);
      if (owner.email) lines.push(`Email: ${owner.email}`);
      if (owner.ownershipPercentage)
        lines.push(`Ownership: ${owner.ownershipPercentage}%`);
      if (owner.citizenshipType)
        lines.push(`Citizenship: ${owner.citizenshipType === "us" ? "US" : "Non-US"}`);
      return lines;
    }),
  };

  // Documents summary
  const visibleDocs = schema ? getVisibleDocuments(schema.documents, data) : [];
  const documentSummary = {
    icon: <FileText className="h-5 w-5" />,
    title: "Documents Uploaded",
    details: Array.isArray(documents)
      ? documents.flatMap(({ type, files }) => {
          const docDef = visibleDocs.find((d) => d.type === type) ||
            schema?.documents?.find((d) => d.type === type);
          const label = docDef?.label || type;
          return (files || []).map((file) => `${label}: ${file.fileName}`);
        })
      : [],
  };

  // Combine all summary sections in order
  const summaryItems = [];
  // Business info first
  const businessStep = flatStepSummaries.find((s) => s.title === "Business Information");
  if (businessStep) summaryItems.push(businessStep);
  // DBAs
  summaryItems.push(dbaSummary);
  // Owners
  summaryItems.push(ownerSummary);
  // Remaining flat steps (banking, processing)
  for (const step of flatStepSummaries) {
    if (step.title !== "Business Information") summaryItems.push(step);
  }
  // Documents
  summaryItems.push(documentSummary);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-normal text-foreground mb-2">
          Registration Summary<span className="text-brand">.</span>
        </h2>
        <p className="text-muted-foreground font-light">
          Review your information before submitting. Our experts will use this
          to recommend the best payment processors for your business.
        </p>
      </div>

      {/* What Happens Next */}
      <div className="bg-muted/30 border border-brand/30 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-normal text-brand mb-3">
          What Happens Next?
        </h3>
        <div className="space-y-2 text-muted-foreground font-light">
          {[
            "Our payment processing experts analyze your business profile",
            "We identify the best processors for your industry and transaction patterns",
            "You receive personalized recommendations with competitive rates",
            "We handle all applications and negotiations on your behalf",
          ].map((text, i) => (
            <div key={i} className="flex items-center">
              <div className="w-6 h-6 bg-brand/20 border border-brand rounded-full flex items-center justify-center mr-3 text-sm font-semibold text-brand">
                {i + 1}
              </div>
              <span>{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {summaryItems.map((item, index) => (
          <div
            key={index}
            className="bg-card border border-border rounded-lg p-6"
          >
            <div className="flex items-center mb-3">
              <div className="text-brand mr-3">{item.icon}</div>
              <h3 className="text-lg font-normal text-foreground">{item.title}</h3>
            </div>
            <div className="space-y-1">
              {item.details.length > 0 ? (
                item.details.map((detail, detailIndex) => (
                  <p
                    key={detailIndex}
                    className="text-sm text-muted-foreground font-light"
                  >
                    {detail}
                  </p>
                ))
              ) : (
                <p className="text-sm text-muted-foreground italic font-light">
                  No information provided
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Submit Button */}
      <div className="text-center pt-6">
        <button
          onClick={onSubmit}
          className="inline-block px-8 py-3 bg-brand text-background text-lg font-semibold rounded-lg hover:bg-brand-400 transition"
        >
          Submit Registration
        </button>
        <p className="mt-2 text-sm text-muted-foreground font-light">
          Our experts will review your information and contact you within 24
          hours with personalized recommendations.
        </p>
      </div>
    </div>
  );
};

export default RegistrationSummary;
